import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";

/** Plays a short synthesized beep using Web Audio API (no asset needed). */
function playBeep() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const beepAt = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    beepAt(880, 0, 0.18);
    beepAt(1174, 0.2, 0.22);
    setTimeout(() => ctx.close(), 700);
  } catch (e) {
    // silent
  }
}

const LS_KEY = "rps_last_seen_booking";
const LS_SOUND = "rps_notif_sound";

export default function BookingNotifier({ onOpenBookings }) {
  const [count, setCount] = useState(0);
  const [latest, setLatest] = useState([]);
  const [open, setOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem(LS_SOUND) !== "off");
  const lastSeenRef = useRef(localStorage.getItem(LS_KEY) || new Date().toISOString());
  const prevCountRef = useRef(0);

  const poll = async () => {
    try {
      const { data } = await api.get("/admin/bookings/notifications", {
        params: { since: lastSeenRef.current },
      });
      setCount(data.count);
      setLatest(data.latest || []);
      if (data.count > prevCountRef.current && soundEnabled) {
        playBeep();
      }
      prevCountRef.current = data.count;
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    poll();
    const id = setInterval(poll, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundEnabled]);

  const markSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem(LS_KEY, now);
    lastSeenRef.current = now;
    setCount(0);
    setLatest([]);
    prevCountRef.current = 0;
  };

  const handleClickBooking = (id) => {
    markSeen();
    setOpen(false);
    if (onOpenBookings) onOpenBookings();
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem(LS_SOUND, next ? "on" : "off");
  };

  return (
    <div className="relative" data-testid="notif-bell-wrapper">
      <button
        onClick={() => setOpen(!open)}
        data-testid="notif-bell-btn"
        aria-label="Notifikasi booking baru"
        className={`relative w-10 h-10 flex items-center justify-center border transition-colors ${
          count > 0
            ? "border-[#FF0055] text-[#FF0055] bg-[#FF0055]/10 animate-pulse"
            : "border-white/10 text-gray-400 hover:border-white/30 hover:text-white"
        }`}
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#FF0055] text-white text-[10px] font-black"
            data-testid="notif-badge"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-[#0F0F11] border border-white/10 shadow-2xl z-50"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#00E5FF] font-semibold">Notifikasi</div>
              <div className="font-heading font-bold text-white text-sm mt-0.5">
                {count > 0 ? `${count} booking baru` : "Semua terbaca"}
              </div>
            </div>
            <button
              onClick={toggleSound}
              data-testid="notif-sound-toggle"
              className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 border transition-colors ${
                soundEnabled ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/10 text-gray-500"
              }`}
              title="Toggle sound"
            >
              {soundEnabled ? "🔔 ON" : "🔕 OFF"}
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {latest.length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-500">Tidak ada booking baru</div>
            ) : (
              latest.map((b) => (
                <button
                  key={b.id}
                  onClick={() => handleClickBooking(b.id)}
                  data-testid={`notif-item-${b.id}`}
                  className="w-full text-left p-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white text-sm truncate">{b.customer_name}</div>
                      <div className="text-xs text-gray-400 truncate">{b.unit_name}</div>
                    </div>
                    <div className="text-xs text-[#00E5FF] font-bold whitespace-nowrap">
                      Rp {Number(b.total_price || 0).toLocaleString("id-ID")}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {count > 0 && (
            <button
              onClick={markSeen}
              data-testid="notif-mark-read"
              className="w-full py-2.5 text-xs uppercase tracking-widest text-gray-400 hover:text-white border-t border-white/10 transition-colors"
            >
              Tandai semua terbaca
            </button>
          )}
        </div>
      )}
    </div>
  );
}
